import { useState, useRef } from "react";
import type { ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import { Upload, AlertCircle, CheckCircle } from "lucide-react";
import Modal from "@/components/ui/Modal";
import {
  SessionUploadInfoCard,
  FileUploadDropZone,
  UploadErrorAlert,
  UploadModalActions,
} from "@/components/sessions/UploadModalParts";
import { apiClient } from "@/services/api";
import {
  TelemetryParseJobCancelledError,
  TelemetryParseJobTimeoutError,
} from "@/services/api/telemetry";
import type { Session } from "@/types";
import { getFileExtension } from "@/utils/fileUtils";

interface DataUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  session: Session;
  onUploadSuccess?: () => void;
}

type UploadStatus = "idle" | "uploading" | "processing" | "success" | "error";

interface ApiErrorLike {
  response?: {
    data?: { error?: { message?: string }; message?: string };
  };
}

export default function DataUploadModal({
  isOpen,
  onClose,
  session,
  onUploadSuccess,
}: DataUploadModalProps) {
  const { t } = useTranslation(["pages", "common"]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>("idle");
  const [error, setError] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [overwriteLaps, setOverwriteLaps] = useState(false);
  const [lapCreationResult, setLapCreationResult] = useState<{
    created: number;
    errors: number;
    totalLaps: number;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Bumped on every close/reset so an in-flight upload/poll for a previous
  // open of the modal can detect it has been orphaned and stop silently.
  const pollSessionRef = useRef(0);

  const acceptedFileTypes = [".xrk", ".xrz", ".csv"];
  const maxFileSize = 100 * 1024 * 1024; // 100MB

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const fileExtension = getFileExtension(file.name);
    if (!acceptedFileTypes.includes(fileExtension)) {
      setError(
        t("pages:sessions.upload.data.errorInvalidFileType", {
          types: acceptedFileTypes.join(", "),
        }),
      );
      return;
    }

    // Validate file size
    if (file.size > maxFileSize) {
      setError(t("pages:sessions.upload.data.errorFileSize"));
      return;
    }

    setSelectedFile(file);
    setError("");
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    const uploadToken = pollSessionRef.current;

    setUploadStatus("uploading");
    setUploadProgress(0);
    setError("");
    setLapCreationResult(null);

    try {
      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 100);

      // Upload file to backend: CSV parses inline (200); XRK/XRZ are queued
      // (202) and parsed by the backend worker — poll until it finishes.
      const uploadResponse = await apiClient.uploadTelemetryFile(
        session.id,
        selectedFile,
      );

      clearInterval(progressInterval);
      setUploadStatus("processing");

      if (uploadResponse.async) {
        const job = await apiClient.pollTelemetryParseJob(session.id, {
          isCancelled: () => pollSessionRef.current !== uploadToken,
        });
        // The modal was closed while polling — a newer flow may own the
        // modal state now, so bail out without touching anything.
        if (pollSessionRef.current !== uploadToken) return;
        if (job.status !== "succeeded") {
          throw new Error(
            job.error ||
              t("pages:sessions.upload.data.errorUploadProcess"),
          );
        }
      }
      setUploadProgress(100);

      // Always create laps from telemetry data (unified behavior)
      try {
        const lapResult = await apiClient.createLapsFromTelemetry(session.id, {
          overwrite: overwriteLaps,
        });

        setLapCreationResult({
          created: lapResult.created,
          errors: lapResult.errors,
          totalLaps: lapResult.telemetryInfo.totalLaps,
        });
      } catch (lapError: unknown) {
        console.error("Error creating laps:", lapError);
        setError(
          t("pages:sessions.upload.data.errorLapCreation", {
            error:
              (lapError as Error).message ||
              t("pages:sessions.upload.data.errorUnknown"),
          }),
        );
      }

      setUploadStatus("success");

      // Call success callback if provided
      if (onUploadSuccess) {
        onUploadSuccess();
      }

      // Auto-close after success — skip when the user closed in the meantime
      setTimeout(() => {
        if (pollSessionRef.current !== uploadToken) return;
        onClose();
        resetState();
      }, 3000);
    } catch (err: unknown) {
      // Orphaned flow: the modal was closed/reset while this ran — a new
      // flow owns the state, so stay silent.
      if (err instanceof TelemetryParseJobCancelledError) return;
      setUploadStatus("error");
      if (err instanceof TelemetryParseJobTimeoutError) {
        setError(t("pages:sessions.upload.data.errorUploadProcess"));
        return;
      }
      // Prefer the backend's error message (e.g. 409 "already processing").
      const apiMessage =
        (err as ApiErrorLike).response?.data?.error?.message ||
        (err as ApiErrorLike).response?.data?.message;
      setError(
        apiMessage ||
          (err as Error).message ||
          t("pages:sessions.upload.data.errorUploadProcess"),
      );
    }
  };

  const resetState = () => {
    pollSessionRef.current += 1;
    setSelectedFile(null);
    setUploadStatus("idle");
    setError("");
    setUploadProgress(0);
    setOverwriteLaps(false);
    setLapCreationResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleClose = () => {
    onClose();
    resetState();
  };

  const getStatusIcon = () => {
    switch (uploadStatus) {
      case "uploading":
      case "processing":
        return (
 <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600"></div>
        );
      case "success":
 return <CheckCircle className="h-5 w-5 text-green-600" />;
      case "error":
 return <AlertCircle className="h-5 w-5 text-red-600" />;
      default:
 return <Upload className="h-5 w-5 text-secondary-400" />;
    }
  };

  const getStatusMessage = () => {
    switch (uploadStatus) {
      case "uploading":
        return t("pages:sessions.upload.data.uploading", {
          progress: uploadProgress,
        });
      case "processing":
        return t("pages:sessions.upload.data.processing");
      case "success":
        return lapCreationResult
          ? t("pages:sessions.upload.data.successLaps", {
              created: lapCreationResult.created,
              totalLaps: lapCreationResult.totalLaps,
            })
          : t("pages:sessions.upload.data.success");
      case "error":
        return t("pages:sessions.upload.common.uploadFailed");
      default:
        return t("pages:sessions.upload.data.selectFile");
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t("pages:sessions.upload.data.title")}
      size="lg"
    >
      <div className="space-y-6">
        <SessionUploadInfoCard session={session} />

        <FileUploadDropZone
          isIdle={uploadStatus === "idle"}
          isUploading={uploadStatus === "uploading"}
          selectedFile={selectedFile}
          uploadProgress={uploadProgress}
          statusIcon={getStatusIcon()}
          statusMessage={getStatusMessage()}
          hint={t("pages:sessions.upload.common.dragDrop")}
          acceptedFileTypes={acceptedFileTypes}
          fileInputRef={fileInputRef}
          onFileSelect={handleFileSelect}
        />

        {/* Lap Creation Options */}
        {selectedFile && uploadStatus === "idle" && (
 <div className="bg-green-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-green-900 mb-3">
              {t("pages:sessions.upload.data.optionsTitle")}
            </h4>
            <div className="space-y-3">
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={overwriteLaps}
                  onChange={(e) => setOverwriteLaps(e.target.checked)}
                  className="form-checkbox h-4 w-4 text-green-600"
                />
                <span className="text-sm text-green-800">
                  <strong>
                    {t("pages:sessions.upload.data.overwriteLaps")}
                  </strong>
                </span>
              </label>
            </div>
            <p className="text-xs text-green-700 mt-2">
              {t("pages:sessions.upload.data.autoCreateHint")}
            </p>
          </div>
        )}

        {/* File Type Info */}
 <div className="bg-primary-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-primary-900 mb-2">
            {t("pages:sessions.upload.data.supportedTitle")}
          </h4>
          <ul className="text-xs text-primary-800 space-y-1">
            <li>
              <strong>.csv:</strong>{" "}
              {t("pages:sessions.upload.data.csvDesc")}
            </li>
            <li>
              <strong>.xrk:</strong>{" "}
              {t("pages:sessions.upload.data.xrkDesc")}
            </li>
            <li>
              <strong>.xrz:</strong>{" "}
              {t("pages:sessions.upload.data.xrzDesc")}
            </li>
          </ul>
          <p className="text-xs text-primary-700 mt-1">
            {t("pages:sessions.upload.data.maxFileSize")}
          </p>
        </div>

        <UploadErrorAlert error={error} />

        <UploadModalActions
          busy={uploadStatus === "uploading" || uploadStatus === "processing"}
          succeeded={uploadStatus === "success"}
          showIdleActions={uploadStatus === "idle"}
          canSubmit={!!selectedFile}
          submitLabel={t("pages:sessions.upload.data.submit")}
          onBrowse={() => fileInputRef.current?.click()}
          onSubmit={handleUpload}
          onClose={handleClose}
        />
      </div>
    </Modal>
  );
}
